import React, { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { inject, observer } from "mobx-react";

import Title from "../Components/Title";
import PlaylistCard from "../Components/PlaylistCard";
import RecentUsers from "../Components/RecentUsers";
import PlaylistFormModal from "../Components/PlaylistFormModal";
import SongFormModal from "../Components/SongFormModal";
import DeletePlaylistModal from "../Components/DeletePlaylistModal";
import SharePlaylistModal from "../Components/SharePlaylistModal";
import { uploadContext } from "../Contexts/UploadContext";
import JobList from "../Components/JobList";
import { useIsMobile } from "../hooks/useIsMobile";
import { showToast } from "../Utils/toast";
import { usePlausible } from "../hooks/usePlausible";

export default inject(
  "PlaybackStore",
  "PlaylistStore",
  "RecentUsersStore"
)(
  observer(function Home({ PlaybackStore, PlaylistStore, RecentUsersStore, onTogglePlaylist }) {
    const navigate = useNavigate();
    const { jobs, checkJobs, clearJobs } = useContext(uploadContext);
    const isMobile = useIsMobile();
    const { trackEvent } = usePlausible();

    useEffect(() => {
      PlaylistStore.loadUserPlaylists();
      // eslint-disable-next-line
    }, []);

    const handlePlaylistSelect = (loadPlaylistFn) => {
      loadPlaylistFn();
      if (isMobile && onTogglePlaylist) {
        onTogglePlaylist(false);
      }
    };

    // Modal handlers
    const openCreateModal = () => {
      PlaylistStore.modalState.create = true;
    };

    const openSongModal = () => {
      PlaylistStore.modalState.addSong = true;
    };

    const openEditModal = (playlist) => {
      PlaylistStore.editingPlaylist = playlist;
      PlaylistStore.modalState.edit = true;
    };

    const openDeleteModal = (playlist) => {
      PlaylistStore.deleteTarget = playlist;
      PlaylistStore.modalState.delete = true;
    };

    // CRUD handlers with toast notifications
    const handleCreatePlaylist = async (data) => {
      try {
        await showToast.promise(
          PlaylistStore.addPlaylist(data.name),
          {
            loading: 'Creating playlist...',
            success: `"${data.name}" created successfully!`,
            error: 'Failed to create playlist',
          }
        );
        trackEvent('Create Playlist', { name: data.name });
        PlaylistStore.modalState.create = false;
        await PlaylistStore.loadUserPlaylists();
      } catch (error) {
        // Error already shown by toast.promise
      }
    };

    const handleUpdatePlaylist = async (data) => {
      const playlistId = PlaylistStore.editingPlaylist.id;
      try {
        // Map form field 'name' to backend field 'title'
        const updateData = { title: data.name };
        await showToast.promise(
          PlaylistStore.updatePlaylist(playlistId, updateData),
          {
            loading: 'Saving changes...',
            success: `"${data.name}" updated successfully!`,
            error: 'Failed to update playlist',
          }
        );
        trackEvent('Edit Playlist', { playlistId, name: data.name });
        PlaylistStore.modalState.edit = false;
        PlaylistStore.editingPlaylist = null;
        await PlaylistStore.loadUserPlaylists();
      } catch (error) {
        // Error already shown by toast.promise
      }
    };

    const handleDeletePlaylist = async () => {
      const playlist = PlaylistStore.deleteTarget;
      try {
        await showToast.promise(
          PlaylistStore.deletePlaylist(playlist.id),
          {
            loading: 'Deleting playlist...',
            success: `"${playlist.title}" deleted`,
            error: 'Failed to delete playlist',
          }
        );
        trackEvent('Delete Playlist', { playlistId: playlist.id, title: playlist.title });
        PlaylistStore.modalState.delete = false;
        PlaylistStore.deleteTarget = null;
        await PlaylistStore.loadUserPlaylists();
      } catch (error) {
        // Error already shown by toast.promise
      }
    };

    const handleQuickPlay = async (playlistId, playlistName) => {
      try {
        await PlaylistStore.quickPlayPlaylist(playlistId, PlaybackStore);
        trackEvent('Quick Play Playlist', { playlistId, playlistName });
        showToast.success(`Playing "${playlistName}"`);
        if (isMobile && onTogglePlaylist) {
          onTogglePlaylist(false);
        }
      } catch (error) {
        showToast.error('Failed to load playlist');
      }
    };

    const handleQuickPlayPublic = async () => {
      try {
        await PlaylistStore.quickPlayPublicPlaylist(PlaybackStore);
        trackEvent('Quick Play Playlist', { playlistId: 'public', playlistName: 'Public' });
        showToast.success('Playing Public playlist');
        if (isMobile && onTogglePlaylist) {
          onTogglePlaylist(false);
        }
      } catch (error) {
        showToast.error('Failed to load playlist');
      }
    };

    const handleQuickPlayMySongs = async () => {
      try {
        await PlaylistStore.quickPlayUserSongs(RecentUsersStore.currentUser.userId, PlaybackStore);
        trackEvent('Quick Play Playlist', { playlistId: 'my-songs', playlistName: 'My Songs' });
        showToast.success('Playing My Songs');
        if (isMobile && onTogglePlaylist) {
          onTogglePlaylist(false);
        }
      } catch (error) {
        showToast.error('Failed to load playlist');
      }
    };

    const handleAddSong = async (query, playlist, isPublic) => {
      try {
        await PlaylistStore.addSong(query, playlist, isPublic);
        trackEvent('Add Song', { query, playlist, isPublic });
        PlaylistStore.modalState.addSong = false;
        checkJobs(() => {
          PlaylistStore.loadPlaylist();
        });
        showToast.success('Song queued for processing');
      } catch (error) {
        showToast.error("There was an error while fetching media info: " + error.message);
        console.log(error);
      }
    };

    const playlistCount = PlaylistStore.userPlaylists.length;
    const friendCount = RecentUsersStore.list?.length ?? 0;

    const headerActions = (
      <>
        <button
          onClick={openSongModal}
          className="flex items-center gap-1 px-2 h-7 rounded-md bg-neutral-800 border border-neutral-700 text-text-muted hover:text-text hover:bg-neutral-700 transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-accent"
          aria-label="Add song"
        >
          <svg className="w-3 h-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
          </svg>
          <span className="text-[10px] font-semibold uppercase tracking-wider">Song</span>
        </button>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-1 px-2 h-7 rounded-md bg-accent border border-blue-400 text-white hover:bg-accent-hover transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-accent"
          aria-label="Add playlist"
        >
          <svg className="w-3 h-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
          </svg>
          <span className="text-[10px] font-semibold uppercase tracking-wider">Playlist</span>
        </button>
      </>
    );

    return (
      <div className="flex-1 flex-col bg-primary px-4 md:px-6 py-4 md:py-5">
        {/* Job List - Shows upload/processing progress */}
        {jobs.length > 0 && (
          <section className="mb-5 md:mb-6">
            <JobList list={jobs} onClear={clearJobs} />
          </section>
        )}

        {/* My Music Section */}
        <section className="mb-6 md:mb-7">
          <Title
            eyebrow="Collection"
            label="My Music"
            subtitle={`${playlistCount} ${playlistCount === 1 ? 'playlist' : 'playlists'}`}
            action={headerActions}
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 md:gap-3">
            {/* All Playlists - now includes virtual playlists from API */}
            {PlaylistStore.userPlaylists.map((playlist) => {
              // Determine load function based on playlist type
              let loadFunction;
              let quickPlayHandler;

              if (playlist.id === "public") {
                loadFunction = () => PlaylistStore.loadPlaylist();
                quickPlayHandler = handleQuickPlayPublic;
              } else if (playlist.id === "my-songs") {
                loadFunction = () => PlaylistStore.loadUserPlayList(RecentUsersStore.currentUser.userId);
                quickPlayHandler = handleQuickPlayMySongs;
              } else {
                loadFunction = () => PlaylistStore.loadPlaylist(playlist.id);
                quickPlayHandler = () => handleQuickPlay(playlist.id, playlist.title);
              }

              // Determine sharing indicators
              const isShared = playlist.isShared || false;
              const sharedBy = playlist.sharedBy || null;
              const accessLevel = playlist.accesslevel || null;

              // Determine share status based on playlist shares
              let shareStatus = 'private';
              if (playlist.shareCount) {
                shareStatus = playlist.hasEveryoneShare ? 'everyone' : 'specific';
              }

              return (
                <PlaylistCard
                  key={playlist.id}
                  title={playlist.title}
                  thumbnails={playlist.thumbnailUrls}
                  onSelect={() => handlePlaylistSelect(loadFunction)}
                  onPlay={quickPlayHandler}
                  onEdit={
                    !playlist.isVirtual && (!isShared || accessLevel === 'manage')
                      ? () => openEditModal(playlist)
                      : undefined
                  }
                  onShare={
                    !playlist.isVirtual && !isShared
                      ? () => PlaylistStore.openShareModal(playlist)
                      : undefined
                  }
                  onDelete={
                    !playlist.isVirtual && !isShared
                      ? () => openDeleteModal(playlist)
                      : undefined
                  }
                  isShared={isShared}
                  sharedBy={sharedBy}
                  shareStatus={shareStatus}
                  accessLevel={accessLevel}
                />
              );
            })}
          </div>
        </section>

        {/* Friend's Music Section */}
        <section>
          <Title
            eyebrow="Discover"
            label="Friends"
            subtitle={`${friendCount} ${friendCount === 1 ? 'person' : 'people'} listening`}
          />
          <RecentUsers
            userInfoList={RecentUsersStore.list}
            loadUserPlayList={(userId) =>
              handlePlaylistSelect(() => PlaylistStore.loadUserPlayList(userId))
            }
            onSelectUser={(username) => {
              navigate(`/app/u/${username}`);
            }}
          />
        </section>

        {/* Modals */}
        <SongFormModal
          isOpen={PlaylistStore.modalState.addSong}
          onClose={() => (PlaylistStore.modalState.addSong = false)}
          onSubmit={handleAddSong}
          userPlaylists={PlaylistStore.userPlaylists}
          isLoading={PlaylistStore.pushingSong}
        />

        <PlaylistFormModal
          isOpen={PlaylistStore.modalState.create}
          onClose={() => (PlaylistStore.modalState.create = false)}
          onSubmit={handleCreatePlaylist}
          mode="create"
          initialValues={{ name: '' }}
        />

        <PlaylistFormModal
          isOpen={PlaylistStore.modalState.edit}
          onClose={() => {
            PlaylistStore.modalState.edit = false;
            PlaylistStore.editingPlaylist = null;
          }}
          onSubmit={handleUpdatePlaylist}
          mode="edit"
          initialValues={
            PlaylistStore.editingPlaylist
              ? {
                  name: PlaylistStore.editingPlaylist.title,
                }
              : { name: '' }
          }
        />

        <DeletePlaylistModal
          isOpen={PlaylistStore.modalState.delete}
          onClose={() => {
            PlaylistStore.modalState.delete = false;
            PlaylistStore.deleteTarget = null;
          }}
          onConfirm={handleDeletePlaylist}
          playlistName={PlaylistStore.deleteTarget?.title || ''}
        />

        <SharePlaylistModal />
      </div>
    );
  })
);
